import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';
import {
    ItemMeta,
    StructureSection,
    StructureSectionHeader,
    StructureSectionTitle,
    StructureSummaryCard,
    StructureSummaryGrid,
    StructureSummaryLabel,
    StructureSummaryValue,
    StructureTransitionPanel,
    StructureTransitionWrapper,
    StructureTree,
    StructureTreeToggle,
    TreeIcon,
    TreeManageLink,
    TreePrintArea,
    TreePrintAreaDim,
    TreeSide,
    TreeSideFlush,
    TreeVariant,
    TreeVariantHeader
} from '../common/styled';

interface ProductColor {
    colorID: number;
    name: string;
    sides?: Array<{
        sideID?: number;
        name?: string;
        ppcm?: number;
        areas?: Array<{ name?: string; boundsWidth?: number; boundsHeight?: number }>;
    }>;
}

export interface CustomizerStructureSectionProps {
    productId: number;
    productStructureSummary: { totalVariants: number; maxSides: number; maxPrintAreas: number };
    productColors: ProductColor[];
    allProductColors: ProductColor[];
    onManageProduct: () => void;
    onPrintAreaClick?: (colorID: number, sideID: number) => void;
}

export const CustomizerStructureSection: FC<CustomizerStructureSectionProps> = ({
    productId,
    productStructureSummary,
    productColors,
    allProductColors,
    onManageProduct,
    onPrintAreaClick
}) => {
    const [showStructureTree, setShowStructureTree] = useState(false);
    const [expandedVariants, setExpandedVariants] = useState<Set<number>>(new Set());

    const treeColors = productColors.length > 0 ? productColors : allProductColors;
    const isNoVariants = productColors.length === 0;

    return (
        <StructureSection>
            <StructureSectionHeader>
                <StructureSectionTitle>{T._('Product Structure', 'Admin')}</StructureSectionTitle>
                <StructureTreeToggle onClick={() => setShowStructureTree(!showStructureTree)}>
                    {showStructureTree ? '▴' : '▾'} {T._('Details', 'Admin')}
                </StructureTreeToggle>
            </StructureSectionHeader>
            <StructureTransitionWrapper>
                <StructureTransitionPanel $visible={!showStructureTree}>
                    <StructureSummaryGrid>
                        <StructureSummaryCard>
                            <StructureSummaryValue>{productStructureSummary.maxSides}</StructureSummaryValue>
                            <StructureSummaryLabel>
                                {productStructureSummary.maxSides === 1 ? T._('Side', 'Admin') : T._('Sides', 'Admin')}
                            </StructureSummaryLabel>
                        </StructureSummaryCard>
                        <StructureSummaryCard>
                            <StructureSummaryValue>{productStructureSummary.maxPrintAreas}</StructureSummaryValue>
                            <StructureSummaryLabel>
                                {productStructureSummary.maxPrintAreas === 1 ? T._('Print Area', 'Admin') : T._('Print Areas', 'Admin')}
                            </StructureSummaryLabel>
                        </StructureSummaryCard>
                        <StructureSummaryCard>
                            {productStructureSummary.totalVariants === 0 ? (
                                <StructureSummaryLabel>{T._('No variants', 'Admin')}</StructureSummaryLabel>
                            ) : (
                                <>
                                    <StructureSummaryValue>{productStructureSummary.totalVariants}</StructureSummaryValue>
                                    <StructureSummaryLabel>
                                        {productStructureSummary.totalVariants === 1 ? T._('Variant', 'Admin') : T._('Variants', 'Admin')}
                                    </StructureSummaryLabel>
                                </>
                            )}
                        </StructureSummaryCard>
                    </StructureSummaryGrid>
                </StructureTransitionPanel>
                <StructureTransitionPanel $visible={showStructureTree}>
                    <StructureTree>
                        {isNoVariants && treeColors.length === 1
                            ? (treeColors[0].sides || []).map((side, sIdx) => {
                                  const areas = side.areas || [];
                                  const ppcm = side.ppcm;
                                  return (
                                      <React.Fragment key={sIdx}>
                                          <TreeSideFlush>
                                              <TreeIcon>&#9724;</TreeIcon>
                                              <span>{side.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                          </TreeSideFlush>
                                          {areas.map((area, aIdx) => {
                                              const dimLabel =
                                                  ppcm && area.boundsWidth && area.boundsHeight
                                                      ? `${(area.boundsWidth / ppcm).toFixed(1)} × ${(area.boundsHeight / ppcm).toFixed(1)} cm`
                                                      : null;
                                              return (
                                                  <TreePrintArea
                                                      key={aIdx}
                                                      $clickable
                                                      onClick={() =>
                                                          onPrintAreaClick && side.sideID
                                                              ? onPrintAreaClick(treeColors[0].colorID, side.sideID)
                                                              : onManageProduct()
                                                      }
                                                  >
                                                      <TreeIcon>&#9679;</TreeIcon>
                                                      <span>{area.name || `${T._('Print Area', 'Admin')} ${aIdx + 1}`}</span>
                                                      {dimLabel && <TreePrintAreaDim>{dimLabel}</TreePrintAreaDim>}
                                                  </TreePrintArea>
                                              );
                                          })}
                                      </React.Fragment>
                                  );
                              })
                            : treeColors.map((color) => {
                                  const isExpanded = expandedVariants.has(color.colorID);
                                  const sides = color.sides || [];
                                  const variantDomId = `variant-${color.colorID}`;
                                  const toggleExpand = () => {
                                      const willExpand = !expandedVariants.has(color.colorID);
                                      setExpandedVariants((prev) => {
                                          const next = new Set(prev);
                                          if (willExpand) next.add(color.colorID);
                                          else next.delete(color.colorID);
                                          return next;
                                      });
                                      if (willExpand) {
                                          setTimeout(() => {
                                              document.getElementById(variantDomId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                          }, 80);
                                      }
                                  };
                                  return (
                                      <TreeVariant key={color.colorID} id={variantDomId}>
                                          <TreeVariantHeader $clickable={sides.length > 0} onClick={sides.length > 0 ? toggleExpand : undefined}>
                                              <TreeIcon>{sides.length > 0 ? (isExpanded ? '▾' : '▸') : '–'}</TreeIcon>
                                              <span>{color.name}</span>
                                              <ItemMeta>
                                                  {sides.length} {sides.length === 1 ? T._('side', 'Admin') : T._('sides', 'Admin')}
                                              </ItemMeta>
                                          </TreeVariantHeader>
                                          {isExpanded &&
                                              sides.map((side, sIdx) => {
                                                  const areas = side.areas || [];
                                                  const ppcm = side.ppcm;
                                                  return (
                                                      <React.Fragment key={sIdx}>
                                                          <TreeSide>
                                                              <TreeIcon>&#9724;</TreeIcon>
                                                              <span>{side.name || `${T._('Side', 'Admin')} ${sIdx + 1}`}</span>
                                                          </TreeSide>
                                                          {areas.map((area, aIdx) => {
                                                              const dimLabel =
                                                                  ppcm && area.boundsWidth && area.boundsHeight
                                                                      ? `${(area.boundsWidth / ppcm).toFixed(1)} × ${(area.boundsHeight / ppcm).toFixed(1)} cm`
                                                                      : null;
                                                              return (
                                                                  <TreePrintArea
                                                                      key={aIdx}
                                                                      $clickable
                                                                      onClick={() =>
                                                                          onPrintAreaClick && side.sideID
                                                                              ? onPrintAreaClick(color.colorID, side.sideID)
                                                                              : onManageProduct()
                                                                      }
                                                                  >
                                                                      <TreeIcon>&#9679;</TreeIcon>
                                                                      <span>{area.name || `${T._('Print Area', 'Admin')} ${aIdx + 1}`}</span>
                                                                      {dimLabel && <TreePrintAreaDim>{dimLabel}</TreePrintAreaDim>}
                                                                  </TreePrintArea>
                                                              );
                                                          })}
                                                      </React.Fragment>
                                                  );
                                              })}
                                      </TreeVariant>
                                  );
                              })}
                    </StructureTree>
                    <TreeManageLink>
                        <span onClick={onManageProduct}>{T._('Edit product', 'Admin')}</span>
                    </TreeManageLink>
                </StructureTransitionPanel>
            </StructureTransitionWrapper>
        </StructureSection>
    );
};
